import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getActions, updateAction, getActionStats, ActionUpdateRequest, ActionFilters } from '../services/actionService';
import { useSiteStore } from '../store/site.store';
import { toast } from 'react-toastify';

/**
 * Hook to fetch and manage actions
 * Uses tenantId from selectedSite.organizationId and siteId from selectedSite.id
 */
export const useActions = (
  siteId: number | null | undefined,
  filters?: ActionFilters
) => {
  const queryClient = useQueryClient();
  const selectedSite = useSiteStore((state) => state.selectedSite);
  const tenantId = selectedSite?.organizationId;

  const { data: actions, isLoading, error, refetch } = useQuery({
    queryKey: ['actions', siteId, tenantId, filters],
    queryFn: () => getActions(siteId!, tenantId, filters),
    enabled: !!siteId && !!tenantId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000 // 5 minutes
  });

  const updateMutation = useMutation({
    mutationFn: ({ actionId, request }: { actionId: number; request: ActionUpdateRequest }) =>
      updateAction(siteId!, actionId, request),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['actions', siteId] });
      queryClient.invalidateQueries({ queryKey: ['action-stats', siteId] });
      toast.success('Action updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update action: ${error.message}`);
    }
  });

  const markAsInProgress = (actionId: number) => {
    updateMutation.mutate({
      actionId,
      request: { status: 'IN_PROGRESS' }
    });
  };

  const markAsDone = (actionId: number) => {
    updateMutation.mutate({
      actionId,
      request: { status: 'DONE' }
    });
  };

  const dismissAction = (actionId: number, reason?: string) => {
    updateMutation.mutate({
      actionId,
      request: { status: 'CANCELLED', reason, dismissalReason: reason }
    });
  };

  const reopenAction = (actionId: number, reason?: string) => {
    updateMutation.mutate({
      actionId,
      request: { status: 'PENDING', reason }
    });
  };

  const snoozeAction = (actionId: number, days: number) => {
    updateMutation.mutate({
      actionId,
      request: { status: 'SNOOZED', snoozeDays: days }
    });
  };

  return {
    actions: actions || [],
    isLoading,
    error,
    refetch,
    markAsInProgress,
    markAsDone,
    dismissAction,
    reopenAction,
    snoozeAction,
    isUpdating: updateMutation.isPending
  };
};

/**
 * Hook to fetch action statistics
 */
export const useActionStats = (siteId: number | null | undefined) => {
  const selectedSite = useSiteStore((state) => state.selectedSite);
  const tenantId = selectedSite?.organizationId;

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['action-stats', siteId, tenantId],
    queryFn: () => getActionStats(siteId!, tenantId),
    enabled: !!siteId && !!tenantId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000
  });

  return {
    stats,
    isLoading,
    error
  };
};
